import {
  BookingPayment,
  Confirmation,
  MakePayment,
  HostConfirm,
  ProcessArrow,
  SelectBed,
  SelectTenancy,
  SignAgreement,
  SubmitEnquiry,
  CheckIn,
  Consultant,
} from 'common/components/svgs';
import { makeStyles } from '@material-ui/styles';
import { Popover } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      maxWidth: 700,
      minWidth: 340,
    },
    wrapperBooked: {
      width: 344,
    },
    title: {
      ...theme.typography.h5,
      margin: '0 0 12px',
      color: theme.colors.titleColor,
    },
    description: {
      ...theme.typography.body4,
      color: theme.colors.bodyColor,
    },
    list: {
      ...theme.flex.rowMain,
      marginBottom: 4,
      justifyContent: 'normal',
    },
    li: {
      width: 96,
      maxWidth: 160,
      position: 'relative',
      marginRight: 24,
      paddingTop: 24,

      '&:last-child': {
        marginRight: 0,
      },
    },
    listCenter: {
      justifyContent: 'center',
    },
    forwardIcon: {
      position: 'absolute',
      top: '28px',
      right: '-24px',
      width: 24,
      height: 12,
      fill: theme.colors.yellow6,
    },
    structureIcon: {
      width: 24,
      height: 24,
      margin: 'auto',
      marginBottom: 12,
      textAlign: 'center',
    },
    label: {
      ...theme.typography.body4,
      textAlign: 'center',
      lineHeight: '20px',
      '& span': {
        width: '96px',
        display: 'inline-block',
      },
    },
    reverseWidth: {
      width: '481px',
    },
    entrataTitle: {
      width: 576,
      display: 'inline-block',
    },
  }),
  { name: 'BookingJourneyStructures' },
);

const PopoverContent = ({ description, list = [], type }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div
      className={classNames(classes.wrapper, {
        [classes.wrapperBooked]: list?.length === 2,
        [classes.reverseWidth]: type === 'instant',
      })}
    >
      <h5 className={classes.title}>{t('how_student_book')}</h5>
      <span
        className={classNames(classes.description, {
          [classes.entrataTitle]: type === 'lead_generation',
        })}
      >
        {description}
      </span>
      <div className={classNames(classes.list, { [classes.listCenter]: type === 'booked' })}>
        <For each="item" of={list} index="index">
          <div key={item.label} className={classes.li}>
            <div className={classes.structureIcon}>{item.icon}</div>
            <div className={classes.label}>
              <span>{item.label}</span>
            </div>
            <If condition={index + 1 < list.length}>
              <ProcessArrow className={classes.forwardIcon} />
            </If>
          </div>
        </For>
      </div>
    </div>
  );
};
PopoverContent.propTypes = {
  description: PropTypes.string.isRequired,
  list: PropTypes.array,
  type: PropTypes.oneOf(['hercules', 'yugo', 'booked', 'instant']),
};
const BookingJourneyStructures = ({ children, type, ...otherProps }) => {
  const { t } = useTranslation();

  const herculesMessageProperty = [
    { label: t('select_tenancy'), icon: <SelectTenancy /> },
    { label: t('personal_information'), icon: <HostConfirm /> },
    { label: t('select_payment_plan'), icon: <BookingPayment /> },
    { label: t('waiting_for_property_confirmation'), icon: <Confirmation /> },
    { label: t('make_payment'), icon: <MakePayment /> },
  ];

  const yugoIntegratedBookings = [
    { label: t('select_tenancy'), icon: <SelectTenancy /> },
    { label: t('select_bed'), icon: <SelectBed /> },
    { label: t('select_payment_plan'), icon: <BookingPayment /> },
    { label: t('personal_info_details'), icon: <HostConfirm /> },
    { label: t('sign_agreement'), icon: <SignAgreement /> },
    { label: t('make_payment'), icon: <MakePayment /> },
  ];

  const bookedWithConsultant = [
    { label: t('submit_enquiry_form'), icon: <SubmitEnquiry /> },
    { label: t('s_com_booking_consultant_follow_the_lead'), icon: <Consultant /> },
  ];

  const instantBooks = [
    { label: t('cms:select_listing'), icon: <SelectBed /> },
    { label: t('cms:personal_information'), icon: <HostConfirm /> },
    { label: t('cms:sign_Contract'), icon: <SignAgreement /> },
    { label: t('cms:make_payment'), icon: <MakePayment /> },
  ];

  const leadGeneration = [
    { label: t('cms:submit_equiry'), icon: <SubmitEnquiry /> },
    { label: t('cms:landlord_reach_out'), icon: <Consultant /> },
    { label: t('cms:complete_application'), icon: <HostConfirm /> },
    { label: t('cms:sign_lease'), icon: <SignAgreement /> },
    { label: t('cms:check_in'), icon: <CheckIn /> },
  ];

  const structureDetails = useMemo(() => {
    switch (type) {
      case 'hercules':
        return {
          description: t('booking_journey_structures.hercules_desc'),
          list: herculesMessageProperty,
        };
      case 'yugo':
        return {
          description: t('booking_journey_structures.yugo_desc'),
          list: yugoIntegratedBookings,
        };
      case 'booked':
        return {
          description: t('booking_journey_structures.booked_desc'),
          list: bookedWithConsultant,
        };
      case 'instant':
        return {
          description: t('cms:booking_journey_structures.instantBook_desc'),
          list: instantBooks,
        };
      case 'lead_generation':
        return {
          description: t('cms:booking_journey_structures.lead_generation_desc'),
          list: leadGeneration,
        };
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  if (!structureDetails) {
    return children;
  }

  return (
    <Popover
      placement="bottom"
      content={<PopoverContent {...structureDetails} {...otherProps} type={type} />}
    >
      {children}
    </Popover>
  );
};

BookingJourneyStructures.propTypes = {
  children: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['hercules', 'yugo', 'booked', 'instant']),
};

export default BookingJourneyStructures;
